// src/encounter/soap-ribbon.tsx
//
// PROD-2743 · frame v2 (DARK). The S·O·A·P ribbon: ONE tab per note section, never a tab per
// working step, and NO Wrap-up tab.
//
// Samer, 1 Oct (first-hand): "this is wrong navigation, it is not consistent with keeping the SOAP
// as broader schema for multiple practitioners and specialities as per my contract with the
// template session". The contract (CONTRACT-template-to-encounter-v1 §1a): "The structure never
// changes. Every visit is one continuous note: S · O · A · P, then Wrap-up." So the ribbon lists
// the SECTIONS only, and the section shows every working step it holds (HPI · Risk · ROS …) as a
// block in one scroll (`sectionStepsFor`, encounter-shell-view.tsx). Recorded as the design sheet
// v1.6 amendment.
//
// Ported from the approved design (R6c Patient Workspace, "Note sections" track), as the design
// desk's review asked (DESIGN-DESK CHANGES 1389938):
//   · the tab set is the design's: Wrap-up is the last block of the final section, and that
//     section is the active tab while you are in it. Physician S · O · A · P (TS-3: A and P
//     separate, Samer A6; the one A/P step draws its assessment in A and its plan in P); nurse
//     S · O · P "Nursing plan"; allied health S · O · P "Plan". The grouping is lib/soap-ribbon.ts;
//   · the label 13, no SOAP letter before it (Ahmed 2026-10-07: removed from the tab and the section
//     heading); the active section bold text-primary, the others
//     medium text-secondary; "· N pending" on the section that holds the orders (P);
//   · the 3px rule is an INSET SPAN, not the button's border: left 8 · right 8 · bottom 0 ·
//     height 3 · radius 3 3 0 0, inside a `position: relative` button, filled with the section's
//     wayfinding INK when it is active or complete and transparent otherwise. The span carries
//     `data-wayfind`, so the parity gate measures the same element the design draws;
//   · the completion check is the brand blue (`--accent-primary`; Antonin, 13 Oct: done steps are
//     blue, "make those blue"), not the tab's own colour. Was text-secondary.
//
// Clicking a section goes to its FIRST step through the page's one step handler, so autosave flush and
// the Risk bridge flush run exactly as a step change always has.
//
// TS-3 (Samer, Loom 5 Oct; R6c `noteMode`): in one continuous scroll (`scroll`, the frame v2 default) the
// tabs are JUMP LINKS — a click scrolls the note to the section, and the highlighted tab is the section
// in view, not the page's step. The ✓ is the section's DOCUMENTED state (`doneSections`,
// structured-note/section-completion.ts), never a visit: authority rule 8, "Visiting completes nothing".
import { Check, Minus } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { PaStepKey } from './lib/encounter-steps';
import { groupForRibbon, sectionOf, type SoapSectionId } from './lib/soap-ribbon';

/** Each section's rule in its wayfinding INK as a background, never the fill (S history · O neutral
 *  · A&P problems · P medications), the design's `CATS` and the same categories as the section
 *  badges (`CHROME_SECTION_WAYFIND`, encounter-section-chrome.tsx). Literal strings so Tailwind sees them. */
const RULE_INK: Record<SoapSectionId, string> = {
  s: 'bg-wayfind-history-ink',
  o: 'bg-wayfind-neutral-ink',
  a: 'bg-wayfind-problems-ink',
  plan: 'bg-wayfind-medications-ink',
};
const WAYFIND: Record<SoapSectionId, string> = {
  s: 'history',
  o: 'neutral',
  a: 'problems',
  plan: 'medications',
};

export interface SoapRibbonStep {
  id: PaStepKey;
  label: string;
}

export interface SoapRibbonProps {
  steps: readonly SoapRibbonStep[];
  activeId: PaStepKey;
  onStepChange: (step: PaStepKey) => void;
  /** Orders staged but not sent. > 0 → the section holding the orders (P) reads "· N pending". */
  pendingOrderCount?: number;
  /** [PROD-3376] Sections the template adds with no step of their own (allied health's coded Assessment). */
  extraSections?: readonly SoapSectionId[];
  /** The sections that show ✓: documented, with no open required blank. Absent → none does. */
  doneSections?: ReadonlySet<SoapSectionId>;
  /**
   * The highlighted section, when the host knows better than `activeId` can say: the section in view
   * (one scroll), or A versus P while standing on the one A/P step (TS-3). Absent → `activeId`'s
   * section (`sectionOf`; the A/P step's is P).
   */
  activeSection?: SoapSectionId;
  /** A tab click. Absent → the tab switches to its section's first step through `onStepChange`. */
  onSelectSection?: (id: SoapSectionId) => void;
  /** The tabs are jump links within one continuous scroll (`aria-current="location"`, TS-3). */
  jumpLinks?: boolean;
  /** Sections that need attention → what is missing (up to three). Long page only; absent → no marker. */
  attentionSections?: Partial<Record<SoapSectionId, string[]>>;
  /** sections the practitioner passed ("Nothing to record"): a dash marker, tooltip "Passed", instead of the
   *  ✓ / attention / empty state. Display only. Absent → none. */
  passedSections?: ReadonlySet<SoapSectionId>;
}

export function SoapRibbon({
  steps,
  activeId,
  onStepChange,
  pendingOrderCount,
  extraSections,
  doneSections,
  activeSection,
  onSelectSection,
  jumpLinks,
  attentionSections,
  passedSections,
}: SoapRibbonProps) {
  // 22 Oct (Antonin's review; the export's EncounterStepBar, `STEPS` in EncounterNoteScreen.tsx): the bar is the SOAP sections and
  // nothing else — Subjective · Objective · Assessment · Plan. The chief complaint is part of Subjective (its block opens HPI), there
  // is no Sign note cell (Wrap-up and the Sign are the end of Plan), and Pre-visit is the page before Start, not a cell here. This
  // replaces the 19 Oct prototype bar (Chief complaint · Subjective · HPI · Objective · Assessment & plan · Sign note).
  const groups = groupForRibbon(steps, (s) => s.id, extraSections);
  const nav = steps.map((s) => s.id);
  const highlighted = activeSection ?? (nav.includes(activeId) ? sectionOf(nav, activeId) : undefined);
  return (
    <nav
      aria-label="Note sections"
      data-slot="soap-ribbon"
      /* WRAPS, never scrolls: a tab that does not fit moves whole to the next line (each tab is
         shrink-0), so the last section can never sit off-screen behind a sideways scroll. */
      className="flex flex-row flex-wrap items-stretch gap-1 min-w-0"
    >
      {groups.map((group) => {
        const active = group.id === highlighted;
        const complete = doneSections?.has(group.id) ?? false;
        // The count sits with the orders: the section that draws the A/P's plan part (P).
        const pending = group.apPart === 'plan' && pendingOrderCount != null && pendingOrderCount > 0 ? pendingOrderCount : 0;
        const ruled = active || complete;
        const passed = !complete && (passedSections?.has(group.id) ?? false);
        const attention = passed ? undefined : attentionSections?.[group.id];
        return (
          <button
            key={group.id}
            type="button"
            data-soap-group={group.id}
            data-steps={[...new Set(group.items.map((s) => s.id))].join(' ')}
            data-active={active ? 'true' : 'false'}
            data-attention={attention && attention.length > 0 && !complete ? 'true' : undefined}
            data-passed={passed ? 'true' : undefined}
            title={passed ? 'Passed' : attention && attention.length > 0 && !complete ? `Still needed: ${attention.join(', ')}` : undefined}
            /* A jump link points at a place in this page (`location`); a switch tab is a step. */
            aria-current={active ? (jumpLinks ? 'location' : 'step') : undefined}
            onClick={() => {
              if (onSelectSection) onSelectSection(group.id);
              else if (!active && group.items[0]) onStepChange(group.items[0].id);
            }}
            className={cn(
              'relative shrink-0 h-10 flex flex-row items-center gap-2 px-3 whitespace-nowrap cursor-pointer',
              'bg-transparent border-0',
              'text-size-sm',
              active ? 'font-bold text-text-primary' : 'font-medium text-text-secondary hover:text-text-primary',
            )}
          >
            <span>{group.title}</span>
            {pending > 0 && (
              <span className="text-size-xs font-normal tabular-nums text-text-secondary">· {pending} pending</span>
            )}
            {passed && <Minus className="size-4 text-text-secondary" role="img" aria-label="Passed" />}
            {/* Needs attention (design EncounterStepBar, ap-attention.html, 21-10): an amber ring with an exclamation at the cell's end,
                never red. The cell's title lists what is missing; the glyph itself is decoration. */}
            {attention && attention.length > 0 && !complete ? (
              <svg data-slot="soap-attention" aria-hidden="true" width="16" height="16" viewBox="0 0 10 10">
                <circle cx="5" cy="5" r="4.1" fill="none" strokeWidth="1.3" />
                <path d="M5 2.7 V5.3" strokeWidth="1.3" strokeLinecap="round" />
                <circle data-part="dot" cx="5" cy="7.1" r="0.7" />
              </svg>
            ) : null}
            {complete && <Check className="size-4 text-brand-primary" role="img" aria-label="complete" />}
            {/* The 3px rule (design: left 8 · right 8 · bottom 0 · height 3 · radius 3 3 0 0).
                left-2/right-2 = --spacing-2 (8) and rounded-t-tag = --radius-tag (3). The DS has
                no 3px HEIGHT token (only --width-accent-bar, a width), so the height is h-0.75:
                Tailwind's base spacing × 0.75 = 3px, a named step and no bracket value. */}
            <span
              data-slot="soap-tab-rule"
              data-wayfind={ruled ? WAYFIND[group.id] : undefined}
              aria-hidden="true"
              className={cn(
                'absolute left-2 right-2 bottom-0 h-0.75 rounded-t-tag',
                ruled ? RULE_INK[group.id] : 'bg-transparent',
              )}
            />
          </button>
        );
      })}
    </nav>
  );
}
